import type { IconMapping } from '@onyx/ui/components/colleague/icon';
import { Icon } from '@onyx/ui/components/colleague/icon';
import { getClassName } from '@onyx/ui/style-utils';

import styles from './badge.module.css';

type BadgeSize = 'large' | 'medium' | 'small';
type BadgeStyle = 'primary' | 'secondary' | 'tertiary';
export type BadgeColor =
  | 'neutral'
  | 'informative'
  | 'positive'
  | 'negative'
  | 'warning';

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> & {
  label?: string;
  icon?: IconMapping;
  /** Size: large | medium | small */
  size?: BadgeSize;
  /** Style variant: primary | secondary | tertiary */
  variant?: BadgeStyle;
  /** Color: neutral | informative | positive | negative | warning */
  color?: BadgeColor;
};

export const Badge = ({
  label,
  icon,
  size = 'medium',
  variant = 'primary',
  color = 'neutral',
  children,
  className,
  ...rest
}: BadgeProps) => {
  const sizeMap: Record<BadgeSize, string> = {
    large: styles.sizeLarge,
    medium: styles.sizeMedium,
    small: styles.sizeSmall,
  };

  const iconSizeMap: Record<BadgeSize, number> = {
    large: 18,
    medium: 16,
    small: 0,
  };

  const variantMap: Record<BadgeStyle, string> = {
    primary: styles.variantPrimary,
    secondary: styles.variantSecondary,
    tertiary: styles.variantTertiary,
  };

  const colorMap: Record<BadgeColor, string> = {
    neutral: styles.colorNeutral,
    informative: styles.colorInformative,
    positive: styles.colorPositive,
    negative: styles.colorNegative,
    warning: styles.colorWarning,
  };

  const classNames = getClassName([
    styles.badge,
    sizeMap[size],
    variantMap[variant],
    colorMap[color],
    className,
  ]);

  const shouldShowIcon = !!icon && size !== 'small' && variant !== 'tertiary';
  const shouldShowDot = variant === 'tertiary';

  return (
    <div className={classNames} {...rest}>
      {shouldShowIcon && (
        <span className={styles.iconWrapper}>
          <Icon name={icon} size={iconSizeMap[size]} />
        </span>
      )}
      {shouldShowDot && (
        <div className={getClassName([styles.dot, colorMap[color]])} />
      )}
      {children || label}
    </div>
  );
};
